<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Skeleton from 'primevue/skeleton';
import Tag from 'primevue/tag';
import type { OfferHoldHistoryEntry, OfferManagementRow } from '@apparatix/types/OfferManagement';

type Props = {
  visible: boolean;
  row?: OfferManagementRow;
  history: OfferHoldHistoryEntry[];
  isLoading: boolean;
}

type Emits = {
  (e: 'close'): void;
}

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const isVisible = computed({
  get: () => props.visible,
  set: () => emit('close'),
});

const actionSeverity: Record<OfferHoldHistoryEntry['action'], 'success' | 'info' | 'danger' | 'secondary'> = {
  Added: 'success',
  Changed: 'info',
  Cleared: 'danger',
  Expired: 'secondary',
};

const describeEntry = (entry: OfferHoldHistoryEntry): string => {
  if (entry.action === 'Added') return `Held thru ${entry.to}`;
  if (entry.action === 'Changed') return `${entry.from} → ${entry.to}`;
  if (entry.action === 'Expired') return entry.from ? `Hold thru ${entry.from} expired` : 'Hold expired';
  return entry.from ? `Removed hold thru ${entry.from}` : 'Hold removed';
};
</script>
<template>
  <Dialog
    v-model:visible="isVisible"
    header="Hold History"
    style="width: 33vw"
    :breakpoints="{ '1280px': '50vw', '960px': '75vw', '640px': '95vw' }"
    modal
  >
    <div v-if="row" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-lg font-semibold">{{ row.advertiser }}</span>
        <span class="text-sm text-[var(--black-80)]">Proposal {{ row.proposalId }} &middot; {{ row.holdThru ? `Held thru ${row.holdThru}` : 'No hold' }}</span>
      </div>
      <div v-if="isLoading" class="flex flex-col gap-2">
        <Skeleton v-for="index in 3" :key="index" height="2.5rem" />
      </div>
      <p v-else-if="history.length === 0" class="text-sm font-normal">
        No hold changes have been recorded for this offer.
      </p>
      <ul v-else class="flex flex-col divide-y divide-[var(--black-16)]">
        <li v-for="(entry, index) in history" :key="index" class="flex items-start justify-between gap-4 py-2">
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <Tag :value="entry.action" :severity="actionSeverity[entry.action]" />
              <span class="text-sm">{{ describeEntry(entry) }}</span>
            </div>
            <span v-if="entry.isInferred" class="text-xs italic text-[var(--black-80)]">Not logged when it happened; inferred from the offer's current hold.</span>
          </div>
          <div class="flex shrink-0 flex-col items-end text-sm">
            <span class="font-semibold">{{ entry.userName }}</span>
            <span class="text-[var(--black-80)]">{{ entry.at ?? '—' }}</span>
          </div>
        </li>
      </ul>
      <div class="flex justify-end">
        <Button severity="secondary" size="small" @click="emit('close')">
          Close
        </Button>
      </div>
    </div>
  </Dialog>
</template>
